import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { roleManagemenUrl } from "../../../config";
import DynamicForm from "../../../data/Axios/DynamicForm";
import { subRightsFields } from "../../../data/DynamicTable/AccountsDynamicForms";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";

const base_url = roleManagemenUrl.uri;

const SubRightsForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);

  const [rightsData, setRightsData] = useState([]);
  const [allSubRightsData, setAllSubRightsData] = useState([]);
  const [usedDisplayOrders, setUsedDisplayOrders] = useState([]);
  const [suggestedOrder, setSuggestedOrder] = useState(1);
  const [selectedRightId, setSelectedRightId] = useState(
    props.data ? props.data.RightId : ""
  );

  const initialValues = {
    id: props.data ? props.data.Id : "",
    title: props.data ? props.data.Title : "",
    icon: props.data ? props.data.Icon : "",
    action: props.data ? props.data.Action : "",
    rightId: props.data ? props.data.RightId : "",
    displayOrder: props.data ? props.data.DisplayOrder ?? "" : "",
    companyId: props.data ? props.data.CompanyId : "",
    updatedDate: props.data
      ? props.data.UpdatedDate
      : new Date().toISOString().split("T")[0],
  };

  // Fetch rights (for the dropdown) and all subrights (to compute used display orders) once
  useEffect(() => {
    async function fetchAllData() {
      try {
        const rightsResponse = await fetchJsonDynamic("allRights", null, null, base_url);
        if (rightsResponse?.results) {
          setRightsData(JSON.parse(rightsResponse.results) || []);
        }

        const subRightsResponse = await fetchJsonDynamic("allSubRights", null, null, base_url);
        if (subRightsResponse?.results) {
          setAllSubRightsData(JSON.parse(subRightsResponse.results) || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchAllData();
  }, []);

  // Pure computation — returns the used list, doesn't rely on stale state
  const computeUsedOrders = (rightId, subRightsList) => {
    if (!rightId || !subRightsList.length) return [];
    return subRightsList
      .filter(
        (sr) =>
          sr.RightId === rightId &&
          (!props.isEditing || sr.Id !== props.data.Id) // exclude self when editing
      )
      .map((sr) => sr.DisplayOrder)
      .filter((n) => n !== null && n !== undefined)
      .sort((a, b) => a - b);
  };

  // Recompute whenever the selected right or the subrights list changes
  useEffect(() => {
    const used = computeUsedOrders(selectedRightId, allSubRightsData);
    setUsedDisplayOrders(used);
    setSuggestedOrder(used.length ? Math.max(...used) + 1 : 1);
  }, [selectedRightId, allSubRightsData]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { title, rightId, displayOrder } = values;
      const parsedOrder = displayOrder !== "" ? parseInt(displayOrder) : null;

      // Recompute fresh at submit time so this doesn't depend on onChange having fired
      const freshUsed = computeUsedOrders(rightId, allSubRightsData);

      if (parsedOrder !== null && freshUsed.includes(parsedOrder)) {
        const confirm = await Swal.fire({
          icon: "warning",
          title: "Number already in use",
          text: `Display order ${parsedOrder} is already used by another sub right under this right. It will be swapped with this one's current position. Continue?`,
          showCancelButton: true,
        });
        if (!confirm.isConfirmed) {
          setSubmitting(false);
          return;
        }
      }

      const mutationData = props.isEditing
        ? {
            id: props.data.Id,
            title,
            createdBy: userId,
            rightId,
            displayOrder: parsedOrder,
            isActive: 1,
            companyId: props.data.CompanyId,
            adminOnly: false,
          }
        : {
            title,
            rightId,
            displayOrder: parsedOrder,
            isActive: 1,
            adminOnly: false,
          };

      const response = await executeAnyMutation(
        props.isEditing ? "updateSubRight" : "createSubRight",
        mutationData,
        props.isEditing ? "updatedSubRight" : "newSubRight",
        false,
        base_url
      );

      if (Object.values(response.results)) {
        props.onClose();
        props.onAction();

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Sub Right ${props.isEditing ? "updated" : "created"} successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }

    setSubmitting(false);
  };

  const rightOptions = rightsData.map((right) => ({
    value: right.Id,
    label: right.Title,
  }));

  const fields = [
    ...subRightsFields,
    {
  name: "rightId",
  label: "Right *",
  type: "select",
  options: rightOptions,
  gridColumn: "span 2",
  required: true,
  skipInternalOnChange: true, // ⭐ required or field.onChange below is ignored
  onChange: (event, value) => setSelectedRightId(value), // ⭐ signature is (event, value)
},
    {
      name: "displayOrder",
      label: `Display Order (suggested: ${suggestedOrder})`,
      type: "number",
      gridColumn: "span 2",
      helperText: usedDisplayOrders.length
        ? `Already used: ${usedDisplayOrders.join(", ")}`
        : "No display order set yet for this right's sub rights",
    },
  ];

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      onSubmit={handleSubmit}
      title={props.isEditing ? "Edit Sub Right" : "Create Sub Right"}
      subtitle={
        props.isEditing
          ? "Edit an Existing Sub Right"
          : "Create a New Sub Right"
      }
    />
  );
};

export default SubRightsForm;